// File: @/hooks/use-scroll-tracking.ts

'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { scrollDepthEvent, track } from '@/lib/analytics';

// ===================================
// TYPES
// ===================================

export interface UseScrollTrackingOptions {
  /** Content identifier for tracking */
  readonly contentId: string;

  /** Content category */
  readonly category: string;

  /** Scroll milestones to track (defaults to [25, 50, 75, 90]) */
  readonly milestones?: readonly number[];

  /** Whether tracking is enabled (defaults to true) */
  readonly enabled?: boolean;
}

// ===================================
// CONSTANTS
// ===================================

const DEFAULT_MILESTONES = [25, 50, 75, 90] as const;

// ===================================
// HOOK
// ===================================

/**
 * Hook for tracking scroll depth milestones
 *
 * Why this exists:
 * - Reusable scroll tracking across pages
 * - Automatic cleanup on unmount
 * - Prevents duplicate milestone tracking
 *
 * When to use:
 * - Pages with scrollable content
 * - Articles, match details, court information
 * - Automated by PageAnalytics component
 *
 * Performance:
 * - Uses passive scroll listeners (no blocking)
 * - Ref-based state (no re-renders)
 * - Set-based deduplication (no duplicate events)
 *
 * @param options - Scroll tracking configuration
 *
 * @example
 * useScrollTracking({
 *   contentId: 'homepage',
 *   category: 'landing',
 * });
 */
export function useScrollTracking(options) {
  const $ = _c(7);
  const {
    contentId,
    category,
    milestones: t0,
    enabled: t1
  } = options;
  const milestones = t0 === undefined ? DEFAULT_MILESTONES : t0;
  const enabled = t1 === undefined ? true : t1;
  const scrollDepthRef = useRef(0);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const trackedMilestonesRef = useRef(t2);
  let t3;
  let t4;
  if ($[1] !== category || $[2] !== contentId || $[3] !== enabled || $[4] !== milestones) {
    t3 = () => {
      if (!enabled) {
        return;
      }
      const handleScroll = () => {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollHeight <= 0) {
          return;
        }
        const scrollTop = window.pageYOffset;
        const scrollPercentage = Math.round(scrollTop / scrollHeight * 100);
        if (scrollPercentage > scrollDepthRef.current) {
          scrollDepthRef.current = scrollPercentage;
          milestones.forEach(milestone => {
            if (scrollPercentage >= milestone && !trackedMilestonesRef.current.has(milestone)) {
              trackedMilestonesRef.current.add(milestone);
              track(scrollDepthEvent(milestone as 25 | 50 | 75 | 90, contentId, category));
            }
          });
        }
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t4 = [contentId, category, milestones, enabled];
    $[1] = category;
    $[2] = contentId;
    $[3] = enabled;
    $[4] = milestones;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
}